import * as React from 'react';

import { uniqueId } from 'lodash';

import Button from 'components/ButtonDeprecated';
import Field from 'components/Field';
import Label from 'components/Label';
import createChainedFunction from 'utils/createChainedFunction';

import './styles/FormElement.scss';

type Props = {
  /* The underlying element, can be anything
   * that implements a onChange props.
   */
  element: React.ReactType;
  /* Props to pass to the underlying
   * element.
   */
  elementProps?: any;
  /* Function that is called on every onChange
   * This function should throw Errors when the value
   * is invalid, the Error.message will be displayed.
   */
  validate?: (data: any) => void;
  /* Function called when an onChange occured
   * and props.validate did not throw any errors.
   */
  onValidChange?: (data: any) => void;
  /* Function called when an onChange occured
   * and props.validate threw an error.
   */
  onInvalidChange?: (data: any) => void;
  /* When true, the error will not wait for a
   * blur event before being visible.
   */
  showErrorBeforeBlur?: boolean;
  /* The label of the field.
   * Usually a string, but can be anything.
   */
  label: React.ReactNode;
  /* The "note" prop on the Label component
   */
  labelNote?: React.ReactNode;
  /* Hide label if accompanying text already
   * describes input.
   */
  labelHidden?: boolean;
  /* Subtext that appears under the Field when
   * there is no error
   */
  subtext?: React.ReactNode;
  /* Button that appears to the right of the
   * form element
   */
  appendedButton?: React.ReactElement<typeof Button>;
  /* If true, renders loading indicator
   */
  loading?: boolean;
  /* Error that renders even if the field is
   * valid according to the validation assertions
   */
  manualError?: string | null;
  /* If true, trims whitespace of string values
   * upon a valid change
   */
  trimWhitespace?: boolean;
};

type State = {
  error?: string | null;
  wasBlurred?: boolean;
};

/* Generic version of FormInput, FormSelect ...
 * that can and should be used outside of formsy <Form />.
 *
 * FormElement is a simple <Field> and <Label> wrapper
 * cappable of showing error messages. Think carefully before
 * adding functionality to this component as it can get really shitty really fast.
 */
class FormElement extends React.Component<Props, State> {
  state: State = {};

  _id: string = uniqueId('id-');

  maybeTrimValue = (data: any) => {
    if (this.props.trimWhitespace && typeof data?.target?.value === 'string') {
      return {
        ...data,
        target: {
          ...data.target,
          value: data.target.value.trim(),
        },
      };
    }
    return data;
  };

  handleBlur = () => {
    this.setState({ wasBlurred: true });
  };

  /* The parameter is called data because
   * some elements like Input returns an event but
   * some other return values (like the date picker input).
   * This can remain generic because FormElement doesn't do anything
   * with the value, it just pass it along to props functions.
   */
  handleChange = (data: any) => {
    let error;
    const maybeTrimmedData = this.maybeTrimValue(data);

    if (this.props.validate) {
      try {
        this.props.validate(maybeTrimmedData);
      } catch (e) {
        error = e.message;
      }
    }

    if (error && this.props.onInvalidChange) {
      this.props.onInvalidChange(maybeTrimmedData);
    } else if (!error && this.props.onValidChange) {
      this.props.onValidChange(maybeTrimmedData);
    }

    this.setState({ error });
  };

  render() {
    const Element = this.props.element;
    const {
      elementProps,
      label,
      labelNote,
      labelHidden,
      showErrorBeforeBlur,
      subtext,
      appendedButton,
      loading,
      manualError,
    } = this.props;
    const { wasBlurred } = this.state;
    let { error } = this.state;

    if (!showErrorBeforeBlur && !wasBlurred) {
      error = null;
    }

    const element = (
      <Element
        {...elementProps}
        id={this._id}
        onChange={this.handleChange}
        onBlur={createChainedFunction(this.handleBlur, elementProps && elementProps.onBlur)}
      />
    );

    return (
      <Field
        error={error ?? manualError}
        subtext={subtext}
        loading={loading}
        className="FormElement">
        <Label hidden={labelHidden} htmlFor={this._id} note={labelNote}>
          {label}
        </Label>
        {appendedButton ? (
          <div className="FormElement--withButton">
            {element}
            {appendedButton}
          </div>
        ) : (
          element
        )}
      </Field>
    );
  }
}

export default FormElement;
